import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Layout, Menu, Avatar, Dropdown, message } from 'antd';
import type { MenuProps } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation, useOutlet } from 'react-router-dom';
import { KeepAlive } from 'keepalive-for-react';
import type { KeepAliveRef } from 'keepalive-for-react';
import AuthStatus from '../components/AuthStatus';
import NavTabs from '../components/NavTabs';
import { menuItems } from '../config/menu';
import {
  defaultVisitedTabs,
  getNavTabByPath,
  normalizeTabPath,
  restoreVisitedTabs,
  type NavTabItem,
} from '../config/navTabs';
import './MainLayout.css';

const { Header, Sider, Content } = Layout;
const VISITED_TABS_STORAGE_KEY = 'ka-manage-visited-tabs';

const MainLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const outlet = useOutlet();
  const aliveRef = useRef<KeepAliveRef>();
  const activeTabKey = useMemo(() => normalizeTabPath(location.pathname), [location.pathname]);
  const selectedMenuKey = location.pathname.split('/').filter(Boolean)[0] || 'dashboard';
  const [username, setUsername] = useState('管理员');
  const [showDebug, setShowDebug] = useState(false);
  const [visitedTabs, setVisitedTabs] = useState<NavTabItem[]>(() => {
    const restoredTabs = restoreVisitedTabs(localStorage.getItem(VISITED_TABS_STORAGE_KEY));
    const currentTab = getNavTabByPath(location.pathname);
    return restoredTabs.some((item) => item.key === currentTab.key) ? restoredTabs : [...restoredTabs, currentTab];
  });
  
  useEffect(() => {
    // 检查用户是否已登录
    const userStr = localStorage.getItem('user');
    if (!userStr) {
      console.log('No user found in localStorage, redirecting to login');
      window.location.href = '/login';
    } else {
      try {
        const user = JSON.parse(userStr);
        setUsername(user.username || '管理员');
        console.log('User loaded from localStorage:', user.username);
      } catch (error) {
        console.error('Failed to parse user from localStorage', error);
      }
    }
  }, [navigate]);

  useEffect(() => {
    const nextTab = getNavTabByPath(location.pathname);
    setVisitedTabs((prev) => {
      if (prev.some((item) => item.key === nextTab.key)) return prev;
      return [...prev, nextTab];
    });
  }, [location.pathname]);

  useEffect(() => {
    localStorage.setItem(VISITED_TABS_STORAGE_KEY, JSON.stringify(visitedTabs));
  }, [visitedTabs]);

  const handleMenuClick: MenuProps['onClick'] = (info) => {
    console.log('Menu item clicked:', info.key);
    navigate(`/${info.key}`);
  };

  const closeTabs = useCallback(
    async (removeKeys: string[]) => {
      const uniqueRemoveKeys = Array.from(new Set(removeKeys)).filter((key) => key !== '/dashboard');
      if (uniqueRemoveKeys.length === 0) return;

      const currentIndex = visitedTabs.findIndex((item) => item.key === activeTabKey);
      const nextTabs = visitedTabs.filter((item) => !uniqueRemoveKeys.includes(item.key));
      await aliveRef.current?.destroy(uniqueRemoveKeys);
      setVisitedTabs(nextTabs.length > 0 ? nextTabs : defaultVisitedTabs);

      if (uniqueRemoveKeys.includes(activeTabKey)) {
        const fallbackTab = nextTabs[Math.max(Math.min(currentIndex - 1, nextTabs.length - 1), 0)] || defaultVisitedTabs[0];
        navigate(fallbackTab.path);
      }
    },
    [activeTabKey, navigate, visitedTabs],
  );

  const handleTabAction = useCallback(
    async (action: string) => {
      const activeIndex = visitedTabs.findIndex((item) => item.key === activeTabKey);
      const activeTab = visitedTabs[activeIndex];

      if (action === 'refresh') {
        aliveRef.current?.refresh(activeTabKey);
        return;
      }

      if (action === 'close-current' && activeTab?.closable) {
        await closeTabs([activeTabKey]);
        return;
      }

      if (action === 'close-others') {
        await closeTabs(visitedTabs.filter((item) => item.key !== activeTabKey).map((item) => item.key));
        return;
      }

      if (action === 'close-left') {
        await closeTabs(visitedTabs.slice(0, Math.max(activeIndex, 0)).map((item) => item.key));
        return;
      }

      if (action === 'close-right') {
        await closeTabs(visitedTabs.slice(activeIndex + 1).map((item) => item.key));
        return;
      }

      if (action === 'close-all') {
        await closeTabs(visitedTabs.map((item) => item.key));
        navigate('/dashboard');
      }
    },
    [activeTabKey, closeTabs, navigate, visitedTabs],
  );

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    message.success('退出登录成功');
    window.location.href = '/login';
  };

  const userMenuItems = [
    {
      key: 'debug',
      icon: <LogoutOutlined />,
      label: `${showDebug ? '隐藏' : '显示'}调试面板`,
      onClick: () => setShowDebug(!showDebug),
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout,
    },
  ];

  return (
    <Layout className="main-layout">
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={collapsed}
        width={200}
        className="main-sider"
      >
        <div className="logo">
          {!collapsed && <span>后台管理系统</span>}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedMenuKey]}
          defaultOpenKeys={['product-management', 'operations-management']}
          items={menuItems}
          onClick={handleMenuClick}
        />
      </Sider>
      <Layout className="main-shell">
        <Header className="main-header">
          {React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
            className: 'trigger',
            onClick: () => setCollapsed(!collapsed),
          })}
          <div className="header-right">
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <span className="user-info">
                <Avatar icon={<UserOutlined />} />
                <span className="username">{username}</span>
              </span>
            </Dropdown>
          </div>
        </Header>
        <NavTabs
          activeKey={activeTabKey}
          tabs={visitedTabs}
          onChange={(path) => navigate(path)}
          onClose={(path) => void closeTabs([path])}
          onAction={(action) => void handleTabAction(action)}
        />
        <Content className="main-content">
          <KeepAlive
            activeCacheKey={activeTabKey}
            aliveRef={aliveRef}
            max={16}
            transition
            containerClassName="keep-alive-render"
            cacheNodeClassName="keep-alive-cache-node"
          >
            {outlet}
          </KeepAlive>
          {showDebug && <AuthStatus />}
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout; 
